/**
 * Tiny subscribable store that tracks whether runtime prose translation is
 * currently failing.
 *
 * The translation hooks always degrade to the English source so a page can
 * never go blank — but a silent degrade hides real outages (e.g. a stale
 * `translate-content` deployment returning 401). This store lets the UI render
 * one small inline notice with a retry action instead.
 */

export interface TranslationFailure {
  /** Machine-readable reason returned by the edge function, when available. */
  reason: string;
  /** Deployed function version, when the response exposed it. */
  version?: string | undefined;
  at: number;
}

let current: TranslationFailure | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function subscribeTranslationStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getTranslationFailure(): TranslationFailure | null {
  return current;
}

export function reportTranslationFailure(reason: string, version?: string) {
  current = { reason, version, at: Date.now() };
  emit();
}

export function clearTranslationFailure() {
  if (!current) return;
  current = null;
  emit();
}
